// Pestaña "Vacaciones" del modal de crear/editar empleado.
// Muestra un desglose período-por-período con Generados / Disfrutados / Saldo,
// todos calculados automáticamente. El saldo del período más reciente incluye
// arrastres de saldos positivos de períodos anteriores — ese es el "Saldo de Vacaciones".
import React, { useEffect, useMemo, useState } from "react";
import { Palmtree, Info, RotateCcw, MinusCircle, Trash2 } from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import { calcularPeriodosDetallados, diasVacacionesAnual } from "@/lib/vacaciones";

export default function TabVacacionesEmpleado({ data, empresa, onChange }) {
  const [asistencia, setAsistencia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!data?.id) { if (alive) setAsistencia([]); return; }
        const { data: ast } = await api.get("/asistencia");
        if (alive) setAsistencia(ast || []);
      } catch (e) {
        if (alive) setError("No se pudo cargar la asistencia del empleado.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [data?.id]);

  const empleado = useMemo(() => (data?.id ? data : { ...data, id: "__preview__" }), [data]);
  const { periodos, saldoTotal, diasAnual } = useMemo(
    () => calcularPeriodosDetallados(empleado, asistencia, new Date(), empresa),
    [empleado, asistencia, empresa]
  );

  // Actualiza el override manual de Disfrutados de un período
  const setDisfrutadosManual = (label, valorRaw) => {
    if (!onChange) return;
    const overrides = { ...(data.vacaciones_overrides || {}) };
    if (valorRaw === "" || valorRaw == null) {
      // Vaciar el campo = restaurar valor automático
      if (overrides[label]) {
        delete overrides[label];
      }
    } else {
      const n = Number(valorRaw);
      if (isNaN(n) || n < 0) return;
      overrides[label] = { ...(overrides[label] || {}), disfrutados: n };
    }
    onChange("vacaciones_overrides", overrides);
  };

  const restaurarAuto = (label) => setDisfrutadosManual(label, "");

  // === REBAJOS DE DÍAS GENERADOS ===
  const ajustes = Array.isArray(data.vacaciones_ajustes) ? data.vacaciones_ajustes : [];
  const [showRebajoForm, setShowRebajoForm] = useState(false);
  const [rebajoPeriodo, setRebajoPeriodo] = useState("");
  const [rebajoDias, setRebajoDias] = useState("");
  const [rebajoMotivo, setRebajoMotivo] = useState("");

  const agregarRebajo = () => {
    if (!onChange) return;
    if (!rebajoPeriodo) return alert("Seleccioná un período");
    const n = Number(rebajoDias);
    if (!(n > 0)) return alert("Los días a rebajar deben ser mayores a 0");
    if (!rebajoMotivo.trim()) return alert("Ingresá el motivo del rebajo");
    const nuevo = {
      id: `adj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      periodo_label: rebajoPeriodo,
      dias: n,
      motivo: rebajoMotivo.trim(),
      fecha: new Date().toISOString().slice(0, 10),
    };
    onChange("vacaciones_ajustes", [...ajustes, nuevo]);
    setRebajoPeriodo("");
    setRebajoDias("");
    setRebajoMotivo("");
    setShowRebajoForm(false);
  };

  const eliminarRebajo = (id) => {
    if (!onChange) return;
    if (!window.confirm("¿Eliminar este rebajo? Los días volverán al período.")) return;
    onChange("vacaciones_ajustes", ajustes.filter(a => a.id !== id));
  };

  if (!data?.fecha_ingreso) {
    return (
      <div className="p-6 rounded-2xl bg-[#FFF7E5] border border-[#F0CC8C] flex items-start gap-3">
        <Info size={18} className="text-[#A67115] mt-0.5" />
        <div className="text-sm text-primary-c">
          Ingresá primero la <strong>Fecha de ingreso</strong> en la pestaña <em>Laboral</em>.
          Los períodos de vacaciones se calculan a partir de esa fecha.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tab-vacaciones-empleado">
      {/* Encabezado con fecha de ingreso y política */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-[#FAFAF7] border border-c">
          <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-1">Fecha de ingreso</div>
          <div className="text-lg font-medium text-primary-c">{fmtFecha(data.fecha_ingreso)}</div>
        </div>
        <div className="p-4 rounded-xl bg-[#FAFAF7] border border-c">
          <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-1">Política de la empresa</div>
          <div className="text-lg font-medium text-primary-c">{diasAnual} días por año</div>
          <div className="text-[11px] text-secondary-c mt-0.5">
            {empresa?.actividad_grupo === "comercio_hoteleria" ? "Comercio / Hotelería" : empresa?.actividad_grupo === "servicios_jornadas_especiales" ? "Servicios con Jornadas especiales" : "Servicios / Industria"}
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#E6EFEA] border border-[#C8DACF]">
          <div className="text-xs uppercase tracking-widest text-primary-color/70 font-medium mb-1">Saldo de vacaciones</div>
          <div className="text-2xl font-semibold text-primary-color flex items-baseline gap-1.5">
            <Palmtree size={20} /> {saldoTotal} <span className="text-sm font-normal">{saldoTotal === 1 ? "día" : "días"}</span>
          </div>
          <div className="text-[11px] text-primary-color/70 mt-0.5">Suma de saldos de todos los períodos (incluye el actual)</div>
        </div>
      </div>

      {/* Tabla de períodos */}
      {loading ? (
        <div className="p-6 text-center text-sm text-secondary-c">Cargando historial…</div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-[#FDEDED] border border-[#F2C0BC] text-sm text-error">{error}</div>
      ) : (
        <div className="rounded-xl border border-c overflow-hidden">
          <div className="grid grid-cols-4 bg-[#FAFAF7] border-b border-c text-[11px] uppercase tracking-widest font-semibold text-secondary-c">
            <div className="px-4 py-3">Período</div>
            <div className="px-4 py-3 text-center">Días Generados</div>
            <div className="px-4 py-3 text-center">Días Disfrutados</div>
            <div className="px-4 py-3 text-center">Saldo</div>
          </div>
          {periodos.length === 0 ? (
            <div className="p-6 text-sm text-secondary-c text-center">
              Aún no ha iniciado el primer período de vacaciones.
            </div>
          ) : (
            periodos.map((p) => {
              const negativo = p.saldo < 0;
              return (
                <div
                  key={p.label}
                  className={`grid grid-cols-4 border-t border-c text-sm items-center ${p.esUltimo ? "bg-[#E6EFEA]/40" : ""}`}
                  data-testid={`vac-periodo-${p.label}`}
                >
                  <div className="px-4 py-3">
                    <div className="font-medium text-primary-c">{p.label}</div>
                    <div className="text-[11px] text-secondary-c mt-0.5">
                      {fmtFecha(p.start)} – {fmtFecha(p.end)}
                      {p.esActual && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#FFF7E5] text-[#A67115] text-[9px] uppercase tracking-wider font-semibold">En curso</span>}
                      {p.esUltimo && !p.esActual && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#E6EFEA] text-primary-color text-[9px] uppercase tracking-wider font-semibold">Más reciente</span>}
                    </div>
                  </div>
                  <div className="px-4 py-3 text-center font-medium tabular-nums">
                    {p.generados}
                    {p.totalRebajado > 0 && (
                      <div className="text-[10px] font-normal text-error mt-0.5" title={`Rebajado -${p.totalRebajado} de ${p.generadosAuto}`}>
                        (−{p.totalRebajado} rebajado)
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 flex items-center justify-center gap-1.5">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={p.disfrutados}
                      onChange={(e) => setDisfrutadosManual(p.label, e.target.value)}
                      className={`w-16 text-center py-1 px-1.5 rounded-md border text-sm tabular-nums font-medium ${p.tieneOverride ? "border-[#A67115] bg-[#FFF7E5]" : "border-c bg-white"}`}
                      data-testid={`vac-disfrutados-${p.label}`}
                      disabled={!onChange}
                      title={p.tieneOverride ? `Automático: ${p.disfrutadosAuto}` : "Auto desde asistencia"}
                    />
                    {p.tieneOverride && onChange && (
                      <button
                        type="button"
                        onClick={() => restaurarAuto(p.label)}
                        className="p-1 rounded-md hover:bg-[#FAFAF7] text-secondary-c hover:text-primary-color"
                        title={`Restaurar automático (${p.disfrutadosAuto})`}
                        data-testid={`vac-restore-${p.label}`}
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </div>
                  <div className={`px-4 py-3 text-center font-semibold tabular-nums ${negativo ? "text-error" : (p.esUltimo ? "text-primary-color" : "text-primary-c")}`}>
                    {p.saldo}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Rebajo de días generados + Historial */}
      {onChange && periodos.length > 0 && (
        <div className="rounded-xl border border-c bg-white" data-testid="vac-rebajos-section">
          <div className="p-4 border-b border-c flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-primary-c flex items-center gap-2">
                <MinusCircle size={16} className="text-error" />
                Rebajos de días generados
              </div>
              <div className="text-[11px] text-secondary-c mt-0.5">
                Reducí los días generados de un período con motivo. Queda registrado en el historial.
              </div>
            </div>
            {!showRebajoForm && (
              <button
                type="button"
                onClick={() => setShowRebajoForm(true)}
                className="np-btn np-btn-outline text-xs py-1.5 px-3"
                data-testid="vac-rebajo-nuevo"
              >
                <MinusCircle size={13} className="mr-1.5" /> Nuevo rebajo
              </button>
            )}
          </div>

          {showRebajoForm && (
            <div className="p-4 border-b border-c bg-[#FAFAF7] grid grid-cols-1 md:grid-cols-[1fr_100px_2fr_auto] gap-2 items-end">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-secondary-c font-semibold block mb-1">Período</label>
                <select
                  value={rebajoPeriodo}
                  onChange={e => setRebajoPeriodo(e.target.value)}
                  className="np-input w-full text-sm"
                  data-testid="vac-rebajo-periodo"
                >
                  <option value="">Seleccioná…</option>
                  {periodos.map(p => (
                    <option key={p.label} value={p.label}>{p.label} (gen: {p.generados})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-secondary-c font-semibold block mb-1">Días</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={rebajoDias}
                  onChange={e => setRebajoDias(e.target.value)}
                  className="np-input w-full text-sm text-center"
                  placeholder="0"
                  data-testid="vac-rebajo-dias"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-secondary-c font-semibold block mb-1">Motivo</label>
                <input
                  type="text"
                  value={rebajoMotivo}
                  onChange={e => setRebajoMotivo(e.target.value)}
                  className="np-input w-full text-sm"
                  placeholder="Ej: ausencia injustificada, sanción..."
                  data-testid="vac-rebajo-motivo"
                />
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => { setShowRebajoForm(false); setRebajoPeriodo(""); setRebajoDias(""); setRebajoMotivo(""); }} className="np-btn np-btn-ghost text-xs px-3 py-1.5">Cancelar</button>
                <button type="button" onClick={agregarRebajo} className="np-btn np-btn-primary text-xs px-3 py-1.5" data-testid="vac-rebajo-guardar">Agregar</button>
              </div>
            </div>
          )}

          {ajustes.length === 0 ? (
            <div className="p-4 text-xs text-secondary-c text-center">Sin rebajos registrados.</div>
          ) : (
            <div className="divide-y divide-c">
              {[...ajustes].reverse().map(a => (
                <div key={a.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`vac-rebajo-${a.id}`}>
                  <div className="text-error font-semibold tabular-nums w-14 text-center">−{a.dias}</div>
                  <div className="min-w-[110px]">
                    <div className="font-medium text-primary-c">{a.periodo_label}</div>
                    <div className="text-[10px] text-secondary-c">{fmtFecha(a.fecha)}</div>
                  </div>
                  <div className="flex-1 text-primary-c">{a.motivo}</div>
                  <button
                    type="button"
                    onClick={() => eliminarRebajo(a.id)}
                    className="np-btn np-btn-ghost p-1.5 text-error"
                    title="Eliminar rebajo (los días vuelven al período)"
                    data-testid={`vac-rebajo-del-${a.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="p-4 rounded-xl bg-[#F0F7F3] border border-[#C8DACF] flex items-start gap-3">
        <Info size={16} className="text-primary-color mt-0.5 shrink-0" />
        <div className="text-xs text-primary-c leading-relaxed">
          <strong>Cálculo automático + edición manual.</strong> Los <em>Días Generados</em> corresponden a la política
          de la empresa ({diasAnual} días/año) para períodos cerrados, y a los meses cumplidos para el período en curso.
          Los <em>Días Disfrutados</em> se prellenan automáticamente desde el módulo <em>Vacaciones</em> (registros de asistencia
          con estado Vacaciones que caen en el rango). Podés <strong>sobreescribir</strong> el valor de cualquier período
          escribiendo directamente en el campo — usá el botón <RotateCcw size={11} className="inline align-text-bottom" /> para
          volver al automático. El <strong>Saldo de Vacaciones</strong> es la suma de los saldos de <em>todos</em> los períodos
          (incluye el que está en curso); las <strong>Vencidas</strong> son los saldos de períodos ya cerrados.
        </div>
      </div>
    </div>
  );
}
